import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { SCOPE_LABEL, SCOPE_SHORT, defaultSheet } from '../datasheet';
import EditorView from './EditorView';
import DatasheetPreview from './DatasheetPreview';
import USDatasheetPreview from './USDatasheetPreview';
import Modal from './Modal';
import { exportPagesToPdf } from '../pdfExport';
import { parseNetsuiteFile, sheetFromNetsuiteRow, exportNetsuiteFile } from '../netsuite';
import NetSuitePush from './NetSuitePush';
import USCodeGenerator from './USCodeGenerator';
import { applyCodeToSheet } from '../usds/applyCode';
import { useRegion } from '../region';
import { ensureUs, syncTop } from '../usds/model';

const TABS = [['core', 'Core Range'], ['customer', 'Customer Specific'], ['personal', 'Personal / Project']];
const UNGROUPED = '__ungrouped__';

export default function LibraryView({ sheet, setSheet, currentId, setCurrentId, canEditLibrary, canEditPersonal, canSubmitLibrary, canUsCodegen, isMaster, toast }) {
  const canSave = canEditLibrary || canEditPersonal;
  const canEditScope = (sc) => (sc === 'personal' ? canEditPersonal : (canEditLibrary || canSubmitLibrary));
  const [tab, setTab] = useState('core');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ core: 0, customer: 0, personal: 0 });
  const [families, setFamilies] = useState([]);
  const [openFams, setOpenFams] = useState({});
  const [famModal, setFamModal] = useState(false);
  const [newFam, setNewFam] = useState('');
  const [zoom, setZoom] = useState(0.6);
  const [busy, setBusy] = useState(false);
  const [scopeModal, setScopeModal] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [moveItem, setMoveItem] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [nsPushOpen, setNsPushOpen] = useState(false);
  const [usCodegenOpen, setUsCodegenOpen] = useState(false);
  const [currentScope, setCurrentScope] = useState(null);
  const region = useRegion();
  const previewRef = useRef(null);
  const exportRef = useRef(null);

  const load = useCallback(async () => {
    const all = {};
    for (const [s] of TABS) { const r = await api.listProducts({ scope: s }); all[s] = r.items; }
    setCounts({ core: all.core.length, customer: all.customer.length, personal: all.personal.length });
    let list = all[tab] || [];
    if (q) { const terms = q.toLowerCase().split(/\s+/).filter(Boolean); list = list.filter(p => { const d = p.data || {}; const t = [p.name, p.code, d.variant, p.family, d.internalNote].join(' ').toLowerCase(); return terms.every(x => t.includes(x)); }); }
    setItems(list);
    try { const f = await api.listFamilies(); setFamilies(f.items || []); } catch (e) { console.warn('Families load failed', e); }
  }, [tab, q]);
  useEffect(() => { load(); }, [load]);

  function openItem(p) { setSheet({ ...defaultSheet(), ...p.data, family: p.family || '' }); setCurrentId(p.id); setCurrentScope(p.scope || null); }
  function newSheet() { setSheet(defaultSheet()); setCurrentId(null); setCurrentScope(null); }
  function duplicateItem(p) {
    const base = { ...defaultSheet(), ...p.data, family: p.family || '' };
    base.name = ((p.data?.name || p.name || '') + ' (Copy)').trim();
    setSheet(base); setCurrentId(null); setCurrentScope(null);
    toast('Copy loaded — edit the variant, then Save to keep it as a new datasheet.');
  }

  async function save() {
    if (!canSave) { toast('Read-only — you cannot save.', true); return; }
    if (currentId && currentScope && !canEditScope(currentScope)) {
      toast('View only — ' + SCOPE_LABEL[currentScope] + ' datasheets can\'t be edited by your role.', true); return;
    }
    try {
      if (currentId) { await api.updateProduct(currentId, { name: sheet.name || '(Untitled)', code: sheet.code || '', data: sheet, family: sheet.family || null }); toast('Saved'); load(); }
      else { setScopeModal(true); }
    } catch (e) { toast(e?.response?.data?.detail || 'Save failed', true); }
  }
  async function saveToScope(sc) {
    setScopeModal(false);
    try {
      const row = await api.createProduct({ name: sheet.name || '(Untitled)', code: sheet.code || '', data: sheet, scope: sc, family: sheet.family || null });
      setCurrentId(row.id); setCurrentScope(sc); setTab(sc);
      toast(row.status === 'pending' ? 'Submitted for approval — track it under Datasheets → Approvals' : 'Saved to ' + SCOPE_LABEL[sc]);
      load();
    } catch (e) { toast(e?.response?.data?.detail || 'Save failed', true); }
  }
  async function doDelete() {
    const p = confirmDel; setConfirmDel(null);
    await api.deleteProduct(p.id); if (currentId === p.id) setCurrentId(null); load(); toast('Deleted');
  }
  async function moveToScope(sc) {
    const p = moveItem; setMoveItem(null);
    try {
      await api.updateProduct(p.id, { scope: sc });
      if (p.id === currentId) setCurrentScope(sc);
      const needsApproval = (sc === 'core' || sc === 'customer') && !canEditLibrary;
      toast(needsApproval
        ? 'Moved to ' + SCOPE_LABEL[sc] + ' — submitted for approval'
        : 'Moved to ' + SCOPE_LABEL[sc]);
      setTab(sc); load();
    } catch (e) { toast(e?.response?.data?.detail || 'Move failed', true); }
  }
  async function assignFamily(p, family) {
    await api.updateProduct(p.id, { family: family || null });
    if (p.id === currentId) setSheet(s => ({ ...s, family: family || '' }));
    load();
  }
  async function addFamily() {
    const n = newFam.trim(); if (!n) return;
    await api.createFamily(n); setNewFam(''); load(); toast('Family created');
  }
  async function delFamily(f) {
    await api.deleteFamily(f.id); load(); toast('Family removed');
  }

  async function exportPdf() {
    if (!exportRef.current) return;
    setBusy(true);
    try {
      const fname = (sheet.code || sheet.name || 'datasheet').replace(/[^a-zA-Z0-9_-]+/g, '_') + '.pdf';
      await exportPagesToPdf(exportRef.current, fname, { title: (sheet.name || '') + (sheet.variant ? ' (' + sheet.variant + ')' : '') });
      toast('PDF exported');
    } catch (e) { toast('Export failed: ' + e.message, true); }
    setBusy(false);
  }
  async function importNetsuite(file) {
    if (!file) return;
    try { const row = await parseNetsuiteFile(file); setSheet(s => sheetFromNetsuiteRow(row, s)); toast('Imported from NetSuite file'); }
    catch (e) { toast('Import failed: ' + e.message, true); }
  }
  function exportNetsuite() {
    try { exportNetsuiteFile(sheet); toast('NetSuite .xlsx exported'); }
    catch (e) { toast('Export failed: ' + e.message, true); }
  }

  const itemRow = (p) => (
    <div key={p.id} className={'lib-item' + (p.id === currentId ? ' active' : '')} data-testid={'lib-item-' + p.id} onClick={() => openItem(p)}>
      <div className="nm" title={p.name || '(Untitled)'}>
        <span className="nm-txt">{p.name || '(Untitled)'}</span>
        {(p.data?.internalNote || '').trim() && (
          <span className="lib-note-chip" data-testid={'lib-item-' + p.id + '-note'}
            title={'Internal note (website only):\n\n' + p.data.internalNote}>NOTE</span>
        )}
        {p.code ? <div className="cd">{p.code}</div> : null}
      </div>
      <div className="lib-item-actions">
        {tab === 'core' && canEditLibrary && (
          <select className="fam-assign" data-testid={'fam-assign-' + p.id} value={p.family || ''} onClick={e => e.stopPropagation()} onChange={e => assignFamily(p, e.target.value)} title="Move to family">
            <option value="">— no family</option>
            {families.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
          </select>
        )}
        <span className={'scope-chip scope-' + p.scope} title={SCOPE_LABEL[p.scope]}>{SCOPE_SHORT[p.scope]}</span>
        {canSave && (
          <button className="btn btn-sm" data-testid={'lib-item-' + p.id + '-copy'} onClick={e => { e.stopPropagation(); duplicateItem(p); }} title="Make a copy to edit as a new variant">Copy</button>
        )}
        {region === 'US' && canEditScope(p.scope) && (
          <button className="btn btn-sm" data-testid={'lib-item-' + p.id + '-move'} onClick={e => { e.stopPropagation(); setMoveItem(p); }} title="Move to another library">Move</button>
        )}
        {canEditScope(p.scope) && <button className="btn btn-sm btn-danger" data-testid={'lib-item-' + p.id + '-del'} onClick={e => { e.stopPropagation(); setConfirmDel(p); }} title="Delete">✕</button>}
      </div>
    </div>
  );

  // Core Range: group by family (incl. empty families) + Ungrouped.
  const grouped = {};
  if (tab === 'core') {
    families.forEach(f => { grouped[f.name] = []; });
    items.forEach(p => { const key = p.family && grouped[p.family] !== undefined ? p.family : (p.family || UNGROUPED); (grouped[key] = grouped[key] || []).push(p); });
  }
  const familyOrder = [...families.map(f => f.name).sort(), ...Object.keys(grouped).filter(k => k !== UNGROUPED && !families.find(f => f.name === k)).sort()];

  return (
    <div className="lib-layout">
      <aside className="lib-side">
        <div className="lib-tabs">
          {TABS.map(([s, label]) => (
            <button key={s} className={'lib-tab' + (tab === s ? ' active' : '')} data-testid={'lib-tab-' + s} onClick={() => setTab(s)}>{label}<span className="cnt">{counts[s]}</span></button>
          ))}
        </div>
        <input className="input lib-search" data-testid="lib-search" placeholder="Search name, code, variant, internal note…" value={q} onChange={e => setQ(e.target.value)} />

        {tab === 'core' && canEditLibrary && (
          <div className="fam-bar"><button className="btn btn-sm" data-testid="manage-families" onClick={() => setFamModal(true)}>Manage Families</button></div>
        )}

        <div className="lib-list">
          {tab === 'core' ? (
            (items.length === 0 && families.length === 0) ? <div className="empty-note">{q ? 'No matches.' : 'No families or datasheets yet. Use “Manage Families” to start.'}</div> : (
              <>
                {familyOrder.map(fam => {
                  const list = grouped[fam] || [];
                  const open = openFams[fam] !== false;
                  return (
                    <div className="fam-group" key={fam} data-testid={'fam-group-' + fam}>
                      <button className="fam-head" onClick={() => setOpenFams(s => ({ ...s, [fam]: !open }))}>
                        <span className={'caret' + (open ? ' open' : '')}>▸</span>{fam}<span className="fam-count">{list.length}</span>
                      </button>
                      {open && (list.length ? list.map(itemRow) : <div className="empty-note" style={{ padding: '6px 10px' }}>No datasheets — assign some with the dropdown.</div>)}
                    </div>
                  );
                })}
                {(grouped[UNGROUPED] || []).length > 0 && (
                  <div className="fam-group" data-testid="fam-group-ungrouped">
                    <button className="fam-head" onClick={() => setOpenFams(s => ({ ...s, [UNGROUPED]: !(s[UNGROUPED] !== false) }))}>
                      <span className={'caret' + (openFams[UNGROUPED] !== false ? ' open' : '')}>▸</span>Ungrouped<span className="fam-count">{grouped[UNGROUPED].length}</span>
                    </button>
                    {openFams[UNGROUPED] !== false && grouped[UNGROUPED].map(itemRow)}
                  </div>
                )}
              </>
            )
          ) : (items.length === 0 ? <div className="empty-note">{q ? 'No matches.' : 'Nothing here yet.'}</div> : items.map(itemRow))}
        </div>
        {canSave && (
          <div className="lib-foot">
            <button className="btn btn-sm" data-testid="btn-new" onClick={newSheet}>＋ New</button>
            <button className="btn btn-sm" data-testid="btn-clear" onClick={() => setConfirmClear(true)} title="Reset the editor to a blank datasheet">Clear</button>
            <button className="btn btn-primary btn-sm" data-testid="btn-save" onClick={save}>Save</button>
          </div>
        )}
      </aside>

      <section className="editor-pane">
        {!canSave && <div className="guest-banner">View only — you can browse and read datasheets but not edit or save.</div>}
        {canSave && !canEditLibrary && tab !== 'personal' && (
          <div className="guest-banner" data-testid="library-viewonly-banner">
            {canSubmitLibrary
              ? 'Core Range & Customer Specific saves go through approval — Management or Technical Management review them before they go live.'
              : "View only — Core Range & Customer Specific datasheets can't be edited by your role. Save your own work under Personal / Project."}
          </div>
        )}
        <EditorView sheet={sheet} setSheet={setSheet} />
      </section>

      <section className="preview-pane">
        <div className="preview-toolbar">
          <div className="zoom">
            <button className="btn btn-sm" onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}>−</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button className="btn btn-sm" onClick={() => setZoom(z => Math.min(1, z + 0.1))}>＋</button>
            <button className="btn btn-sm" onClick={() => setZoom(0.6)}>Fit</button>
          </div>
          <div className="preview-actions">
            {region === 'US' && canSave && (canUsCodegen || isMaster) && (
              <button className="btn btn-sm" data-testid="btn-us-codegen" onClick={() => setUsCodegenOpen(true)}
                title="Build a US smart part number">US Code Builder</button>
            )}
            {isMaster && <label className="btn btn-sm" data-testid="btn-import-netsuite" title="Import a NetSuite .xlsx row (master admin only)">NetSuite Import<input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={e => { importNetsuite(e.target.files[0]); e.target.value = ''; }} /></label>}
            {isMaster && <button className="btn btn-sm" data-testid="btn-export-netsuite" onClick={exportNetsuite} title="Export this datasheet as a NetSuite .xlsx row (master admin only)">NetSuite Export</button>}
            {isMaster && <button className="btn btn-sm" data-testid="btn-push-netsuite" onClick={() => setNsPushOpen(true)} title="Review and push this datasheet into NetSuite (master admin only)">Push to NetSuite</button>}
            <button className="btn btn-primary btn-sm" data-testid="btn-export-pdf" onClick={exportPdf} disabled={busy}>{busy ? 'Exporting…' : 'Export PDF'}</button>
          </div>
        </div>
        <div className="preview-scroll">
          <div className="preview-zoom" style={{ transform: `scale(${zoom})` }}>
            <div ref={previewRef}>{region === 'US' ? <USDatasheetPreview sheet={sheet} /> : <DatasheetPreview sheet={sheet} />}</div>
          </div>
        </div>
        <div className="export-host" ref={exportRef} aria-hidden="true">{region === 'US' ? <USDatasheetPreview sheet={sheet} pageId="lib-export" /> : <DatasheetPreview sheet={sheet} pageId="lib-export" />}</div>
      </section>

      {usCodegenOpen && (
        <USCodeGenerator
          initialType={{ housing: 'housing', track: 'track' }[ensureUs(sheet).variant] || 'downlight'}
          onClose={() => setUsCodegenOpen(false)}
          onApply={({ code, typeKey, sel }) => {
            let summary = '';
            setSheet(s => {
              const { us: next, filled } = applyCodeToSheet(ensureUs(s), { code, typeKey, sel });
              summary = filled;
              return { ...s, ...syncTop(next), us: next };
            });
            toast('US code applied: ' + code + ' — ' + summary + ' filled in');
          }}
        />
      )}
      {nsPushOpen && <NetSuitePush entries={[{ key: 'editor', name: sheet.name, sheet, productId: currentId }]} onClose={() => setNsPushOpen(false)} toast={toast} />}

      <Modal open={scopeModal} title="Save datasheet to…" onClose={() => setScopeModal(false)} testid="scope-modal"
        actions={<button className="btn" onClick={() => setScopeModal(false)}>Cancel</button>}>
        <p className="modal-hint">Choose which library this datasheet belongs to.</p>
        <div className="scope-choices">
          {(canEditLibrary || canSubmitLibrary) && <button className="scope-choice" data-testid="scope-core" onClick={() => saveToScope('core')}><strong>Core Range</strong><span>{canEditLibrary ? 'Shared standard products' : 'Submitted for approval first'}</span></button>}
          {(canEditLibrary || canSubmitLibrary) && <button className="scope-choice" data-testid="scope-customer" onClick={() => saveToScope('customer')}><strong>Customer Specific</strong><span>{canEditLibrary ? 'Bespoke / customer items' : 'Submitted for approval first'}</span></button>}
          {canEditPersonal && <button className="scope-choice" data-testid="scope-personal" onClick={() => saveToScope('personal')}><strong>Personal / Project</strong><span>Your working drafts</span></button>}
        </div>
      </Modal>

      <Modal open={!!moveItem} title="Move datasheet to…" onClose={() => setMoveItem(null)} testid="move-scope-modal"
        actions={<button className="btn" onClick={() => setMoveItem(null)}>Cancel</button>}>
        <p className="modal-hint">Move <strong>{moveItem && (moveItem.name || 'Untitled')}</strong> out of {moveItem && SCOPE_LABEL[moveItem.scope]}. The datasheet content, images and code selections stay exactly as they are.</p>
        <div className="scope-choices">
          {TABS.filter(([sc]) => moveItem && sc !== moveItem.scope && canEditScope(sc)).map(([sc, label]) => (
            <button key={sc} className="scope-choice" data-testid={'move-scope-' + sc} onClick={() => moveToScope(sc)}>
              <strong>{label}</strong>
              <span>{sc === 'personal' ? 'Your working drafts' : (canEditLibrary ? (sc === 'core' ? 'Shared standard products' : 'Bespoke / customer items') : 'Submitted for approval first')}</span>
            </button>
          ))}
        </div>
      </Modal>

      <Modal open={!!confirmDel} title="Delete datasheet?" onClose={() => setConfirmDel(null)} testid="confirm-delete-modal"
        actions={<>
          <button className="btn" onClick={() => setConfirmDel(null)}>Cancel</button>
          <button className="btn btn-danger-solid" data-testid="confirm-delete-btn" onClick={doDelete}>Delete</button>
        </>}>
        <p>This will permanently remove <strong>{confirmDel && (confirmDel.name || 'Untitled')}</strong> from the library.</p>
      </Modal>

      <Modal open={confirmClear} title="Clear the editor?" onClose={() => setConfirmClear(false)} testid="confirm-clear-modal"
        actions={<>
          <button className="btn" onClick={() => setConfirmClear(false)}>Cancel</button>
          <button className="btn btn-danger-solid" data-testid="confirm-clear-btn" onClick={() => { setConfirmClear(false); newSheet(); toast('Editor cleared'); }}>Clear</button>
        </>}>
        <p>This resets every field in the editor to blank. Datasheets already saved in the library are not affected.</p>
      </Modal>

      <Modal open={famModal} title="Manage Core Range families" onClose={() => setFamModal(false)} testid="families-modal"
        actions={<button className="btn" onClick={() => setFamModal(false)}>Done</button>}>
        <p className="modal-hint">Create families, then assign datasheets to them using the dropdown on each Core Range item.</p>
        <div className="fam-add">
          <input className="input" data-testid="new-family-name" value={newFam} onChange={e => setNewFam(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addFamily(); }} placeholder="New family name (e.g. Parker)" />
          <button className="btn btn-primary" data-testid="add-family" disabled={!newFam.trim()} onClick={addFamily}>Add</button>
        </div>
        <div className="fam-manage-list">
          {families.length === 0 ? <div className="empty-note">No families yet.</div> :
            families.map(f => (
              <div className="fam-manage-row" key={f.id} data-testid={'fam-row-' + f.id}>
                <span>{f.name}</span>
                <button className="btn btn-sm btn-danger" data-testid={'fam-del-' + f.id} onClick={() => delFamily(f)}>Remove</button>
              </div>
            ))}
        </div>
      </Modal>
    </div>
  );
}
